import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL, getImageUrl } from '../../config';
import { toastSuccess, toastError, errorMessage } from '../../lib/toast';

const JOIN_ERRORS = {
  already_requested: 'You\'ve already requested to join this app.',
  already_participant: 'You\'re already on this team.',
  not_joinable: 'This app isn\'t accepting join requests right now.',
};

// App lifecycle status → user-facing label + card-accent modifier. Mirrors
// MyAppRequests. Explore lists only 'active' apps today, so they all read "In progress",
// but the mapping stays general so the accent is consistent with the rest of the app.
const STATUS_LABEL = {
  pending: 'Pending review',
  approved: 'Approved',
  provisioning: 'Provisioning',
  active: 'In progress',
  publish_requested: 'Publish requested',
  published: 'Published',
  rejected: 'Rejected',
};

// Mandatory, server-injected Google SSO tool — branded with its logo here too,
// matching MyAppRequests / UserAppDetailPage. Detected by name on the custom row.
const SSO_TOOL_NAME = 'Google SSO';
const SSO_TOOL_ICON = '/images/google-sso.png';
const isSsoTool = (t) => t.is_custom && (t.tool_name || '').toLowerCase() === SSO_TOOL_NAME.toLowerCase();

function Avatar({ name, photoUrl, className = '' }) {
  if (photoUrl) {
    return <img className={`ba-ed-avatar ${className}`} src={getImageUrl(photoUrl)} alt="" />;
  }
  return (
    <span className={`ba-ed-avatar ba-ed-avatar--fallback ${className}`} aria-hidden="true">
      {(name || '?').charAt(0).toUpperCase()}
    </span>
  );
}

export default function ExploreApps() {
  const [apps, setApps] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter state
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [selectedToolIds, setSelectedToolIds] = useState([]);
  const [toolOptions, setToolOptions] = useState([]);
  const [toolMenuOpen, setToolMenuOpen] = useState(false);
  // Client-side join-state filter ('all' | 'member' | 'pending' | 'open').
  // Derived from each card's is_participant / my_join_status (already loaded).
  const [joinFilter, setJoinFilter] = useState('all');

  // Drawer state
  const [drawerAppId, setDrawerAppId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  // Join form state (lives inside drawer)
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const mounted = useRef(true);
  const toolMenuRef = useRef(null);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // Debounce: update debouncedQ 300ms after q changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQ(q);
    }, 300);
    return () => clearTimeout(timer);
  }, [q]);

  const load = useCallback(async (currentQ, currentToolIds) => {
    const params = {};
    if (currentQ && currentQ.trim()) params.q = currentQ.trim();
    if (currentToolIds && currentToolIds.length) params.tool_ids = currentToolIds.join(',');

    try {
      const res = await axios.get(`${API_BASE_URL}/api/app_requests/explore`, {
        params,
        withCredentials: true,
      });
      if (mounted.current) { setApps(res.data); setError(null); }
    } catch {
      if (mounted.current) setError('Failed to load apps. Try again.');
    } finally {
      if (mounted.current) setInitialLoading(false);
    }
  }, []);

  // Initial load + re-load when filters change
  useEffect(() => {
    load(debouncedQ, selectedToolIds);
  }, [load, debouncedQ, selectedToolIds]);

  // Fetch tool options once on mount
  useEffect(() => {
    let active = true;
    axios.get(`${API_BASE_URL}/api/app_requests/explore/tool-options`, { withCredentials: true })
      .then((res) => { if (active && mounted.current) setToolOptions(res.data); })
      .catch(() => { /* non-fatal: leave options empty */ });
    return () => { active = false; };
  }, []);

  // Close tools menu on outside click
  useEffect(() => {
    if (!toolMenuOpen) return;
    function handleMouseDown(e) {
      if (toolMenuRef.current && !toolMenuRef.current.contains(e.target)) {
        setToolMenuOpen(false);
      }
    }
    function handleKeyDown(e) {
      if (e.key === 'Escape') setToolMenuOpen(false);
    }
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [toolMenuOpen]);

  const toggleToolId = useCallback((id) => {
    setSelectedToolIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const clearFilters = useCallback(() => {
    setQ('');
    setSelectedToolIds([]);
    setJoinFilter('all');
  }, []);

  const openDrawer = useCallback(async (appId) => {
    setDrawerAppId(appId);
    setDetail(null);
    setDetailError(null);
    setMessage('');
    setDetailLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/app_requests/${appId}/explore-detail`,
        { withCredentials: true }
      );
      if (mounted.current) { setDetail(res.data); setDetailError(null); }
    } catch {
      if (mounted.current) setDetailError('Failed to load this app. Try again.');
    } finally {
      if (mounted.current) setDetailLoading(false);
    }
  }, []);

  const closeDrawer = useCallback(() => {
    if (submitting) return;
    setDrawerAppId(null);
    setDetail(null);
    setDetailError(null);
    setMessage('');
  }, [submitting]);

  const handleCardKey = useCallback((e, appId) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openDrawer(appId);
    }
  }, [openDrawer]);

  const submitJoin = useCallback(async () => {
    if (!drawerAppId) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API_BASE_URL}/api/app_requests/${drawerAppId}/join`,
        { message: message.trim() || undefined },
        { withCredentials: true }
      );
      if (!mounted.current) return;
      toastSuccess('Join request sent.');
      setApps((prev) =>
        prev.map((a) => a.id === drawerAppId ? { ...a, my_join_status: 'pending' } : a)
      );
      setDrawerAppId(null);
      setDetail(null);
      setMessage('');
    } catch (err) {
      toastError(errorMessage(err, 'Couldn\'t send your request. Try again.', JOIN_ERRORS));
    } finally {
      if (mounted.current) setSubmitting(false);
    }
  }, [drawerAppId, message]);

  const hasActiveFilter = q.trim().length > 0 || selectedToolIds.length > 0 || joinFilter !== 'all';

  // Apply the client-side join-state filter on top of the server-filtered list.
  const visibleApps = apps.filter((app) => {
    if (joinFilter === 'member') return app.is_participant;
    if (joinFilter === 'pending') return !app.is_participant && app.my_join_status === 'pending';
    if (joinFilter === 'open') return !app.is_participant && app.my_join_status !== 'pending';
    return true;
  });

  const JOIN_FILTERS = [
    { key: 'all', label: 'All' },
    { key: 'member', label: 'On the team' },
    { key: 'pending', label: 'Request sent' },
    { key: 'open', label: 'Joinable' },
  ];

  if (initialLoading) return <p className="ba-explore-status">Loading apps…</p>;
  if (error) return <p className="ba-error">{error}</p>;

  const isDrawerOpen = drawerAppId !== null;

  return (
    <div className="ba-explore page-fill">
      {/* Filter bar */}
      <div className="ba-explore-filters">
        <input
          className="ba-search-input"
          type="search"
          placeholder="Search apps by name…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Search apps"
        />

        <div className="ba-tools-filter" ref={toolMenuRef}>
          <button
            type="button"
            className={`btn btn-secondary ba-tools-filter-toggle${selectedToolIds.length > 0 ? ' is-active' : ''}`}
            onClick={() => setToolMenuOpen((prev) => !prev)}
            aria-haspopup="listbox"
            aria-expanded={toolMenuOpen}
          >
            <span className="ba-tools-filter-icon" aria-hidden="true">
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                <path d="M2 4h12M4.5 8h7M7 12h2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
            </span>
            Tools
            {selectedToolIds.length > 0 && (
              <span className="ba-tools-filter-count">{selectedToolIds.length}</span>
            )}
          </button>

          {toolMenuOpen && (
            <div className="ba-tools-filter-menu">
              <div className="ba-tools-filter-menu-head">
                <span className="ba-tools-filter-menu-title">Filter by tool</span>
                {selectedToolIds.length > 0 && (
                  <button
                    type="button"
                    className="ba-tools-filter-clear"
                    onClick={() => setSelectedToolIds([])}
                  >
                    Clear
                  </button>
                )}
              </div>
              <div className="ba-tools-filter-list" role="listbox" aria-multiselectable="true">
                {toolOptions.length === 0 ? (
                  <p className="ba-tools-filter-empty">No tools available</p>
                ) : (
                  toolOptions.map((o) => {
                    const checked = selectedToolIds.includes(o.id);
                    return (
                      <label
                        key={o.id}
                        className={`ba-tools-filter-option${checked ? ' is-checked' : ''}`}
                        title={o.name}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleToolId(o.id)}
                        />
                        <span className="ba-tools-filter-option-name">{o.name}</span>
                      </label>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        {/* Join-state segmented filter (client-side, from each card's is_participant / my_join_status). */}
        <div className="ba-join-filter" role="group" aria-label="Filter by join state">
          {JOIN_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`ba-join-filter-btn${joinFilter === f.key ? ' is-active' : ''}`}
              aria-pressed={joinFilter === f.key}
              onClick={() => setJoinFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Active tool-filter chips (removable) so the selection is visible without reopening the menu. */}
      {selectedToolIds.length > 0 && (
        <div className="ba-explore-filter-chips">
          {selectedToolIds.map((id) => {
            const opt = toolOptions.find((o) => o.id === id);
            return (
              <button
                key={id}
                type="button"
                className="ba-explore-filter-chip"
                onClick={() => toggleToolId(id)}
                aria-label={`Remove ${opt ? opt.name : 'tool'} filter`}
              >
                {opt ? opt.name : `Tool #${id}`}
                <span className="ba-explore-filter-chip-x" aria-hidden="true">×</span>
              </button>
            );
          })}
        </div>
      )}

      <div className="page-scroll-body ba-explore-scroll">
      {visibleApps.length === 0 ? (
        hasActiveFilter ? (
          <div className="ba-list-noresults">
            <p>No apps match your filters.</p>
            <button type="button" className="btn btn-secondary" onClick={clearFilters}>
              Clear filters
            </button>
          </div>
        ) : (
          <p className="ba-explore-status">No apps to explore yet.</p>
        )
      ) : (
        <div className="ba-explore-grid">
          {visibleApps.map((app) => (
            <div
              key={app.id}
              className={`ba-explore-card ba-explore-card--clickable ba-card--${app.status}`}
              role="button"
              tabIndex={0}
              aria-label={app.name}
              onClick={() => openDrawer(app.id)}
              onKeyDown={(e) => handleCardKey(e, app.id)}
            >
              <div className="ba-explore-card-head">
                <h3 className="ba-explore-name">{app.name}</h3>
                {app.status && (
                  <span className={`ba-badge ba-badge-${app.status}`}>
                    {STATUS_LABEL[app.status] || app.status}
                  </span>
                )}
              </div>
              {app.description && <p className="ba-explore-desc">{app.description}</p>}
              <div className="ba-explore-meta">
                <span>by {app.requester_name}</span>
                <span>{app.tool_count} tool{app.tool_count === 1 ? '' : 's'}</span>
                <span>{app.collaborator_count} collaborator{app.collaborator_count === 1 ? '' : 's'}</span>
              </div>
              {app.is_participant ? (
                <span className="ba-join-pill ba-join-pill--member">
                  <span className="ba-join-pill-dot" aria-hidden="true" />
                  On the team
                </span>
              ) : app.my_join_status === 'pending' ? (
                <span className="ba-join-pill ba-join-pill--pending">
                  <span className="ba-join-pill-dot" aria-hidden="true" />
                  Request sent
                </span>
              ) : null}
            </div>
          ))}
        </div>
      )}
      </div>

      {/* Drawer backdrop */}
      <div
        className={`ba-explore-drawer-backdrop${isDrawerOpen ? ' is-open' : ''}`}
        onClick={closeDrawer}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <aside
        className={`ba-explore-drawer${isDrawerOpen ? ' is-open' : ''}`}
        aria-label="App details"
        role="dialog"
        aria-modal="true"
      >
        <div className="ba-explore-drawer-header">
          <button
            type="button"
            className="ba-explore-drawer-close"
            onClick={closeDrawer}
            disabled={submitting}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="ba-explore-drawer-body">
          {detailLoading && <p className="ba-explore-status">Loading…</p>}

          {!detailLoading && detailError && (
            <p className="ba-error">{detailError}</p>
          )}

          {!detailLoading && !detailError && detail && (
            <>
              <div className="ba-explore-drawer-titlerow">
                <h2 className="ba-explore-drawer-title">{detail.name}</h2>
                {detail.status && (
                  <span className={`ba-badge ba-badge-${detail.status}`}>
                    {STATUS_LABEL[detail.status] || detail.status}
                  </span>
                )}
              </div>

              {/* Creator row */}
              <div className="ba-explore-drawer-creator">
                <Avatar name={detail.requester_name} photoUrl={detail.requester_photo_url} />
                <span className="ba-explore-drawer-by">by {detail.requester_name}</span>
              </div>

              {/* Description */}
              <p className="ba-explore-drawer-desc">
                {detail.description || <span className="ba-explore-muted">No description.</span>}
              </p>

              {/* Team */}
              <section className="ba-explore-drawer-section">
                <h3 className="ba-explore-drawer-section-title">Team</h3>
                {detail.collaborators.length === 0 ? (
                  <p className="ba-explore-muted">No collaborators yet.</p>
                ) : (
                  <ul className="ba-explore-drawer-team">
                    {detail.collaborators.map((c) => (
                      <li key={c.user_id} className="ba-explore-drawer-member">
                        <Avatar name={c.name} photoUrl={c.photo_url} />
                        <span>{c.name}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              {/* Tools */}
              <section className="ba-explore-drawer-section">
                <h3 className="ba-explore-drawer-section-title">Tools</h3>
                {detail.tools.length === 0 ? (
                  <p className="ba-explore-muted">No tools listed.</p>
                ) : (
                  <ul className="ba-explore-drawer-tools">
                    {/* Pin the mandatory Google SSO pill first so its logo leads, matching the request cards. */}
                    {[...detail.tools].sort((a, b) => (isSsoTool(b) ? 1 : 0) - (isSsoTool(a) ? 1 : 0)).map((t, i) => {
                      const sso = isSsoTool(t);
                      return (
                        <li
                          key={t.tool_id != null ? t.tool_id : `custom-${i}`}
                          className={`ba-tool-pill${sso ? ' ba-tool-pill--sso' : t.is_custom ? ' ba-tool-pill--custom' : ''}`}
                        >
                          {sso ? (
                            <img className="ba-tool-pill-icon" src={getImageUrl(SSO_TOOL_ICON)} alt="" />
                          ) : t.tool_icon_url ? (
                            <img
                              className="ba-tool-pill-icon"
                              src={getImageUrl(t.tool_icon_url)}
                              alt=""
                            />
                          ) : (
                            <span className="ba-tool-pill-icon ba-tool-pill-icon--fallback" aria-hidden="true">
                              {(t.tool_name || '?').charAt(0).toUpperCase()}
                            </span>
                          )}
                          <span className="ba-tool-pill-name">{t.tool_name}</span>
                          {sso ? (
                            <span className="ba-tool-pill-required">Required</span>
                          ) : t.is_custom ? (
                            <span className="ba-tool-pill-custom">Custom</span>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>

              {/* Join area */}
              <div className="ba-explore-drawer-join">
                {detail.is_participant ? (
                  <p className="ba-explore-muted">You're already on this team.</p>
                ) : detail.my_join_status === 'pending' ? (
                  <p className="ba-explore-muted">Request already sent.</p>
                ) : (
                  <>
                    <div className="form-group">
                      <label htmlFor="ba-join-msg">Message (optional)</label>
                      <textarea
                        id="ba-join-msg"
                        rows={3}
                        value={message}
                        placeholder="Optional: how you'd like to help"
                        onChange={(e) => setMessage(e.target.value)}
                        maxLength={1000}
                        disabled={submitting}
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={submitJoin}
                      disabled={submitting}
                    >
                      {submitting ? 'Sending…' : 'Request to join'}
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}
